// Split out of src/components/Tests.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Tests.jsx
// re-exports everything it exported before, so existing imports keep working.

import { t as ct } from "../../lib/content.js";
import { isAnswerKey, Docked, DockContext } from "./shared.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// On-screen keypad used by TTStreaksTest and MathsWorkoutTest instead of a real <input>.
// Nothing here ever focuses a text field, so the OS keyboard never opens — that's the whole
// point: a focused input on mobile pushes/resizes the visual viewport (worse on some browsers
// than others), which is exactly the screen-jump this was built to avoid between questions.
// Every button gets touch-action + tap-highlight overrides directly (not just the wrapping
// grid) — some mobile browsers only suppress the ~300ms synthetic-click delay when the
// interactive element itself carries the property, not just an ancestor.
const KEY_TOUCH_STYLE = { touchAction: "manipulation", WebkitTapHighlightColor: "transparent" };

export function NumericKeypad({ value, onDigit, onBackspace, onDecimal, onToggleSign, onEnter, allowDecimal, allowNegative, disabled }) {
  const btnClass = "bg-slate-100 active:bg-slate-200 rounded-xl py-3 text-lg font-bold text-slate-700 disabled:opacity-40 select-none";

  useEffect(() => {
    if (disabled) return;
    function onKeyDown(e) {
      if (!isAnswerKey(e)) return;
      if (/^[0-9]$/.test(e.key)) { onDigit(e.key); return; }
      // Backspace would otherwise navigate back in some browsers, losing the whole test.
      if (e.key === "Backspace") { e.preventDefault(); onBackspace(); return; }
      if (allowDecimal && (e.key === "." || e.key === ",")) { onDecimal(); return; }
      if (allowNegative && e.key === "-") { onToggleSign(); return; }
      if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [disabled, onDigit, onBackspace, onDecimal, onToggleSign, onEnter, allowDecimal, allowNegative]);

  return (
    <div>
      <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 mb-3 min-h-[44px]">
        <span className="text-lg font-bold text-slate-800">{value || <span className="text-slate-300 font-normal text-base">{ct("tests.numeric_placeholder", "Your answer")}</span>}</span>
        {allowNegative && (
          <button disabled={disabled} aria-label="Plus or minus" onClick={onToggleSign} style={KEY_TOUCH_STYLE} className="text-slate-500 text-base px-2 font-bold">+/&#8211;</button>
        )}
      </div>
      {/* 15 Sep (Alan: "add a decimal point to the number keypad ... just enable the +/- and
          the decimal point at all times on all but TT"). The decimal used to be a grey full
          stop inside the answer READOUT at text-slate-400, off the keypad and easy to miss
          entirely — which is how a child could be served "9.4 + 6.4" with no way to answer it.
          Both controls are now real keys, and both are present at once wherever they apply.
          Measured, properly this time: generateMathsQuestion returns 98 decimal and 59
          negative answers per 4,200 draws, all in strategist. The Arcade warm-up calls that
          same generator on half its questions, so the missing decimal was a dead end in the
          Arcade, not a polish item.
          Four controls, three grid cells. BACKSPACE KEEPS ITS CORNER - an earlier decision,
          stated in the line above, and one I briefly undid before Alan caught it. The SIGN
          toggle takes the readout slot the decimal is vacating instead, on two grounds: it is
          the rarer of the two (59 occurrences against the decimal 98, per 4,200 draws), and a
          decimal is part of typing a number so it belongs among the digits, while a sign flips
          a number already typed and sits fine beside the value it acts on.
          Layout A of four drawn for Alan, 15 Sep. The alternatives were a four-column grid
          (conventional, but ~30% narrower keys on a 360px screen, which is a lot for a
          seven-year-old) and a fifth row with a full-width backspace (easiest to hit, but the
          pad already competes with the question for vertical space on a small phone).
          */}
      <Docked>
      <div className="lb-numpad grid grid-cols-3 gap-2.5" style={{ touchAction: "manipulation" }}>
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} disabled={disabled} onClick={() => onDigit(d)} style={KEY_TOUCH_STYLE} className={btnClass}>{d}</button>
        ))}
        {allowDecimal ? (
          <button disabled={disabled} aria-label="Decimal point" onClick={onDecimal} style={KEY_TOUCH_STYLE} className={btnClass}>.</button>
        ) : <div />}
        <button disabled={disabled} onClick={() => onDigit("0")} style={KEY_TOUCH_STYLE} className={btnClass}>0</button>
        <button disabled={disabled || !value} aria-label="Backspace" onClick={onBackspace} style={KEY_TOUCH_STYLE} className={`${btnClass} text-slate-400`}>⌫</button>
      </div>
      </Docked>
    </div>
  );
}

// On-screen QWERTY keyboard for SpellingTest — same rationale as NumericKeypad above: a
// display readout instead of a focused <input> means the OS keyboard (and its autocomplete
// suggestion bar, which could nudge a kid toward the correct spelling mid-test) never appears.
//
// Aug 2026 feedback round: narrow iPhone portrait was leaving ~24px keys on the 10-letter
// row (well under a comfortable touch target) and typing felt laggy. Two independent fixes,
// neither of which touches the familiar QWERTY layout itself:
//   - Bigger targets: bleeds out to the card's full width (cancelling its own px-3) and uses
//     tighter inter-key gaps, buying back real width without restructuring the rows.
//   - Faster response: registers a key on pointerdown (the instant a finger touches it),
//     not after the full press-release-synthesize-click cycle — see press() below.
export function LetterKeyboard({ value, onLetter, onBackspace, onEnter, disabled, allowSpace = false, placeholder = null, onChange = null, maxLength = Infinity, accents = null }) {
  // Alan, 23 Sep (after LB-368 on his phone): docked, the keys follow the phone's own keyboard
  // in layout and position, in the app's colours and font. a-l inset half a key; the apostrophe
  // beside z and backspace on the right; the phone's bottom row kept as empty tray so the keys
  // sit as high as the phone's own. Check spelling stays in the body (Alan: "the CTA not in the
  // keyboard in all cases").
  const docked = !!React.useContext(DockContext);
  const keyClass = "bg-slate-100 active:bg-slate-200 rounded-lg text-base font-bold text-slate-700 disabled:opacity-40 flex-1 min-w-0 py-3.5 select-none";

  // A movable caret (Alan, 30 Sep: "Allow cursor to be used while the keyboard is in use so the
  // user can select a specific letter to delete from", by touch or by keyboard). Only when the
  // caller hands over the whole value (`onChange`); the older onLetter/onBackspace pair still
  // types and deletes at the end. `caret` null means "at the end", which is where it goes back
  // to whenever the caret reaches the end again or the answer is cleared for the next word, so
  // typing straight through never has to think about it. Tap a letter to put the caret on the
  // side of it you tapped; tap past the end for the end. Arrow keys, Home and End move it on a
  // hardware keyboard; Delete removes the letter after it.
  const editable = typeof onChange === "function";
  const [caret, setCaret] = useState(null);
  const text = value || "";
  const pos = editable && caret != null ? Math.min(caret, text.length) : text.length;
  const live = useRef({});
  live.current = { text, pos };

  // Accents (Alan, 1 Oct: "Same gesture as Apple keyboard - hold down to show list"). `accents`
  // maps a letter to its accented forms (lib/accents.js accentsFor); only with onChange. The
  // letter still types the instant it is touched — the fast-typing rule above is untouched — and
  // holding it HOLD_MS opens a strip of its accents above the key. Slide onto one and let go, or
  // let go and tap one: it REPLACES the letter just typed. Let go anywhere else and the strip
  // stays until the next key or a tap outside it, so a child who lifts too early can still pick.
  // On a hardware keyboard the same strip opens when a key is held (as on a Mac), numbered, and
  // 1-9 picks; an accented key typed straight from the keyboard is taken as it is.
  const accentsOn = editable && !!accents && typeof accents === "object";
  const HOLD_MS = 450;
  const [popup, setPopup] = useState(null); // { base, options, rect, from: "touch"|"key", selected }
  const popupRef = useRef(null);
  popupRef.current = popup;
  const holdRef = useRef(null);
  const heldKeyRef = useRef(null);
  const clearHold = () => { if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; } };
  const accentList = (base) => (accentsOn && Array.isArray(accents[base]) && accents[base].length ? accents[base] : null);
  const openPopup = (base, el, from) => {
    const options = accentList(base);
    if (!options) return;
    const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    setPopup({ base, options, rect: r ? { left: r.left, top: r.top, width: r.width } : null, from, selected: null });
  };
  const startHold = (base, el, from) => {
    clearHold();
    if (!accentList(base)) return;
    holdRef.current = setTimeout(() => { holdRef.current = null; openPopup(base, el, from); }, HOLD_MS);
  };
  // The accent replaces the letter it was opened from when that letter is right before the caret
  // (it always is, unless the caret moved in between); otherwise it is typed at the caret.
  const choose = (ch) => {
    const p = popupRef.current;
    clearHold();
    setPopup(null);
    popupRef.current = null;
    if (!p) return;
    const { text: t, pos: at } = live.current;
    if (at > 0 && t[at - 1] === p.base) {
      const next = t.slice(0, at - 1) + ch + t.slice(at);
      live.current = { text: next, pos: at };
      onChange(next);
    } else typeChar(ch);
  };
  useEffect(() => () => clearHold(), []);
  useEffect(() => { if (disabled) { clearHold(); setPopup(null); } }, [disabled]);
  // A tap anywhere outside the strip closes it (a key tapped then types as usual).
  useEffect(() => {
    if (!popup) return undefined;
    const outside = (e) => { const el = e.target; if (!(el && el.closest && el.closest("[data-accent-popup]"))) setPopup(null); };
    document.addEventListener("touchstart", outside, true);
    document.addEventListener("mousedown", outside, true);
    return () => { document.removeEventListener("touchstart", outside, true); document.removeEventListener("mousedown", outside, true); };
  }, [popup]);
  const place = (p) => { const n = live.current.text.length; const c = Math.max(0, Math.min(p, n)); setCaret(c >= n ? null : c); };
  const typeChar = (ch) => {
    if (popupRef.current) setPopup(null);
    if (!editable) { onLetter(ch); return; }
    const { text: t, pos: at } = live.current;
    if (t.length >= maxLength) return;
    const next = t.slice(0, at) + ch + t.slice(at);
    live.current = { text: next, pos: at + 1 };
    onChange(next);
    setCaret(at >= t.length ? null : at + 1);
  };
  const deleteBack = () => {
    if (popupRef.current) setPopup(null);
    if (!editable) { onBackspace(); return; }
    const { text: t, pos: at } = live.current;
    if (at <= 0) return;
    const next = t.slice(0, at - 1) + t.slice(at);
    live.current = { text: next, pos: at - 1 };
    onChange(next);
    setCaret(at >= t.length ? null : at - 1);
  };
  const deleteForward = () => {
    const { text: t, pos: at } = live.current;
    if (at >= t.length) return;
    const next = t.slice(0, at) + t.slice(at + 1);
    live.current = { text: next, pos: at };
    onChange(next);
    setCaret(at >= next.length ? null : at);
  };

  useEffect(() => {
    if (disabled) return;
    function onKeyDown(e) {
      if (!isAnswerKey(e)) return;
      const open = popupRef.current;
      if (open && open.from === "key") {
        if (/^[1-9]$/.test(e.key) && open.options[Number(e.key) - 1]) { e.preventDefault(); choose(open.options[Number(e.key) - 1]); return; }
        if (e.key === "Escape") { e.preventDefault(); setPopup(null); return; }
      }
      if (/^[a-z']$/i.test(e.key)) {
        const ch = e.key.toLowerCase();
        // Held: a letter with accents does not auto-repeat, it opens the strip (as a Mac does).
        if (e.repeat && accentList(ch)) { e.preventDefault(); return; }
        typeChar(ch);
        if (!e.repeat && accentList(ch)) {
          heldKeyRef.current = ch;
          const keyEl = typeof document !== "undefined" ? document.querySelector(`[data-key="${ch}"]`) : null;
          startHold(ch, keyEl, "key");
        }
        return;
      }
      // An accented letter typed straight from the keyboard (Option-e on a Mac, an international
      // layout): taken as it is, where accents are offered at all.
      if (accentsOn && e.key.length === 1 && /\p{Script=Latin}/u.test(e.key)) { typeChar(e.key.toLowerCase()); return; }
      // LB-364: a homework vocabulary answer can be two words ("the dog"), so the space bar
      // counts as a letter when the caller allows it. Spelling never does.
      if (e.key === " " && allowSpace) { e.preventDefault(); typeChar(" "); return; }
      if (e.key === "Backspace") { e.preventDefault(); deleteBack(); return; }
      if (editable) {
        const { pos: at } = live.current;
        if (e.key === "ArrowLeft") { e.preventDefault(); place(at - 1); return; }
        if (e.key === "ArrowRight") { e.preventDefault(); place(at + 1); return; }
        if (e.key === "Home") { e.preventDefault(); place(0); return; }
        if (e.key === "End") { e.preventDefault(); place(Infinity); return; }
        if (e.key === "Delete") { e.preventDefault(); deleteForward(); return; }
      }
      if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); }
    }
    function onKeyUp(e) {
      if (heldKeyRef.current && String(e.key).toLowerCase() === heldKeyRef.current) { heldKeyRef.current = null; clearHold(); }
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); };
    // typeChar/deleteBack/place read live.current, so the listener never needs the value itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, onLetter, onBackspace, onEnter, onChange, allowSpace, maxLength, accents]);

  // A tap in the readout: on a letter, the caret goes to whichever side of it was tapped; on the
  // readout's empty space, to the start or the end, whichever side of the word it was.
  function tapReadout(e) {
    if (!editable || disabled) return;
    const letter = e.target && e.target.closest ? e.target.closest("[data-caret-at]") : null;
    if (letter) {
      const i = Number(letter.getAttribute("data-caret-at"));
      const r = letter.getBoundingClientRect();
      place(r.width > 0 && e.clientX < r.left + r.width / 2 ? i : i + 1);
      return;
    }
    const first = e.currentTarget.querySelector("[data-caret-at]");
    place(first && e.clientX < first.getBoundingClientRect().left ? 0 : Infinity);
  }
  const caretEl = !disabled ? <span className="lb-caret" aria-hidden="true" data-testid="answer-caret" /> : null;

  // A light haptic tick on every tap. Android Chrome supports the Vibration API; iOS
  // Safari doesn't expose one to web content at all (true of every browser on iOS, since
  // they all sit on WebKit) — this silently no-ops there rather than pretending to support
  // something the platform genuinely doesn't offer a web page.
  function tick() {
    try { if (navigator.vibrate) navigator.vibrate(10); } catch {}
  }

  // Fires the key on the instant a finger touches it, scoped to touch/pen pointers only —
  // registering on pointerdown rather than waiting for click is the actual "quick typing"
  // fix. The FIRST version of this relied on preventDefault() in pointerdown suppressing
  // the browser's follow-up synthetic click for a touch pointer — that assumption turned
  // out to be wrong on at least one real device on staging (every letter typed twice), so
  // it's gone. Instead: any click landing within DUPLICATE_WINDOW_MS of the most recent
  // touch/pen pointerdown is treated as that same tap's compatibility click, not a second
  // separate press, and skipped — regardless of whether THIS particular browser actually
  // suppresses it or not. The window is short enough that two deliberate real taps on the
  // same key (e.g. the double L in "hello") are never eaten — each pointerdown still fires
  // its own action() immediately and independently; this only ever filters the follow-up
  // click, never a follow-up pointerdown. Self-healing by construction: it's a rolling time
  // check, not a flag that can get stuck suppressing a later, unrelated tap forever.
  const DUPLICATE_WINDOW_MS = 300;
  const lastTouchActionRef = useRef(0);

  // Alan, 26 Sep (LB-364 staging, "a general keyboard quirk"): typing fast with two thumbs
  // lost a letter, on Spelling too. The second thumb lands while the first is still down, and
  // WebKit does not reliably dispatch pointerdown for a NON-PRIMARY touch — so the key it hit
  // never fired. touchstart is dispatched for every finger, every time, so a touch now fires
  // its key from touchstart and pointerdown is kept for the pen only. The click filter below
  // is unchanged: a compatibility click within the window of either is skipped.
  //
  // A stylus is the one input that raises BOTH: iPadOS dispatches touch events for an Apple
  // Pencil (Touch.touchType === "stylus") alongside its pen pointerdown, and Android Chrome
  // does the same for a pen without saying so. Without a guard one pencil tap typed its
  // letter twice. So touchstart ignores a stylus touch and anything within the window of a
  // pen pointerdown, and the pen pointerdown ignores anything within the window of a touch
  // that fired — whichever of the pair a browser dispatches first wins, the other is
  // skipped. Only a touchstart that actually FIRED is recorded, so a skipped one can never
  // block the pen's next tap.
  const lastTouchStartRef = useRef(0);
  const lastPenDownRef = useRef(0);
  // `holdKey` (a letter) makes the key a hold target for its accents; see the accents note above.
  // Holding never delays or replaces the press itself: the letter fires exactly as before.
  function press(action, holdKey = null) {
    const releaseHold = () => { clearHold(); const p = popupRef.current; if (p && p.selected) choose(p.selected); };
    return {
      onTouchStart: (e) => {
        if (disabled) return;
        const t = e && e.changedTouches && e.changedTouches[0];
        if (t && t.touchType === "stylus") return;
        const now = Date.now();
        if (now - lastPenDownRef.current < DUPLICATE_WINDOW_MS) return;
        lastTouchStartRef.current = now;
        lastTouchActionRef.current = now;
        tick();
        action();
        if (holdKey) startHold(holdKey, e.currentTarget, "touch");
      },
      // Sliding the held finger onto an accent selects it; letting go there picks it.
      onTouchMove: (e) => {
        if (!popupRef.current || !holdKey) return;
        const t = e && e.touches && e.touches[0];
        const hit = t && typeof document !== "undefined" && document.elementFromPoint ? document.elementFromPoint(t.clientX, t.clientY) : null;
        const opt = hit && hit.closest ? hit.closest("[data-accent]") : null;
        const selected = opt ? opt.getAttribute("data-accent") : null;
        if (popupRef.current.selected !== selected) setPopup((p) => (p ? { ...p, selected } : p));
        popupRef.current = { ...popupRef.current, selected };
      },
      onTouchEnd: releaseHold,
      onTouchCancel: () => clearHold(),
      onPointerDown: (e) => {
        if (disabled) return;
        // A mouse held on a key opens the strip too; its press still comes from the click.
        if (e.pointerType === "mouse") { if (holdKey) startHold(holdKey, e.currentTarget, "touch"); return; }
        if (e.pointerType !== "pen") return;
        const now = Date.now();
        if (now - lastTouchStartRef.current < DUPLICATE_WINDOW_MS) return;
        lastPenDownRef.current = now;
        lastTouchActionRef.current = now;
        tick();
        action();
        if (holdKey) startHold(holdKey, e.currentTarget, "touch");
      },
      onPointerUp: (e) => { if (e.pointerType !== "touch") releaseHold(); },
      onClick: () => {
        if (disabled) return;
        if (Date.now() - lastTouchActionRef.current < DUPLICATE_WINDOW_MS) return;
        action();
      },
    };
  }

  // The strip of accents, over the key it came from (portaled: the dock clips what is inside it).
  const popupEl = (() => {
    if (!popup) return null;
    const w = popup.options.length * 48 + 8;
    const vw = typeof window !== "undefined" ? window.innerWidth || 390 : 390;
    const left = popup.rect ? Math.max(8, Math.min(vw - w - 8, popup.rect.left + popup.rect.width / 2 - w / 2)) : Math.max(8, vw / 2 - w / 2);
    const top = popup.rect ? Math.max(8, popup.rect.top - 62) : 120;
    const strip = (
      <div data-accent-popup="" data-testid="accent-popup" data-from={popup.from} className="fixed z-[70] flex gap-1 bg-white rounded-xl shadow-lg border border-slate-200 p-1" style={{ left, top }}>
        {popup.options.map((ch, i) => (
          <button key={ch} type="button" data-accent={ch} aria-label={ch} {...press(() => choose(ch))} style={KEY_TOUCH_STYLE}
            className={`w-11 h-12 rounded-lg text-lg font-bold flex flex-col items-center justify-center select-none ${popup.selected === ch ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-800"}`}>
            <span className="leading-none">{ch}</span>
            {popup.from === "key" && i < 9 && <span className="text-[10px] font-semibold opacity-60 leading-none mt-0.5" aria-hidden="true">{i + 1}</span>}
          </button>
        ))}
      </div>
    );
    return typeof ReactDOM !== "undefined" && ReactDOM.createPortal && typeof document !== "undefined" ? ReactDOM.createPortal(strip, document.body) : strip;
  })();

  return (
    <div>
      <div className="flex items-center justify-center border border-slate-200 rounded-lg px-3 py-2 mb-3 min-h-[44px]" onClick={tapReadout} data-testid="answer-readout">
        {/* Alan, 26 Sep: a blinking caret says the readout is live and ready for a key. 30 Sep:
            with onChange it sits where the child put it, and each letter is a tap target. */}
        {editable && text ? (
          <span className="text-lg font-bold text-slate-800 tracking-wide whitespace-pre cursor-text">{text.split("").map((ch, i) => (
            <React.Fragment key={i}>{i === pos && caretEl}<span data-caret-at={i}>{ch}</span></React.Fragment>
          ))}{pos >= text.length && caretEl}</span>
        ) : (
          <span className="text-lg font-bold text-slate-800 tracking-wide">{value || <span className="text-slate-300 font-normal text-base tracking-normal">{placeholder || ct("tests.letters_placeholder", "Type what you heard")}</span>}{caretEl}</span>
        )}
      </div>
      {accentsOn && <p className="text-xs text-slate-400 text-center -mt-1.5 mb-2" data-testid="accent-hint">{ct("tests.accent_hint", "Hold a letter for its accents, like \u00e9 or \u00f1")}</p>}
      {popupEl}
      <Docked>
      {docked ? (
      <div className="lb-letters lb-letters-phone flex flex-col" style={{ touchAction: "manipulation" }}>
        <div className="flex">
          {"qwertyuiop".split("").map((ch) => (
            <button key={ch} disabled={disabled} data-key={ch} {...press(() => typeChar(ch), ch)} style={KEY_TOUCH_STYLE} className="flex-1 min-w-0 select-none">{ch}</button>
          ))}
        </div>
        <div className="flex">
          <span className="lb-key-half" aria-hidden="true" />
          {"asdfghjkl".split("").map((ch) => (
            <button key={ch} disabled={disabled} data-key={ch} {...press(() => typeChar(ch), ch)} style={KEY_TOUCH_STYLE} className="flex-1 min-w-0 select-none">{ch}</button>
          ))}
          <span className="lb-key-half" aria-hidden="true" />
        </div>
        <div className="flex">
          <span className="lb-key-gap" aria-hidden="true" />
          <button disabled={disabled} {...press(() => typeChar("'"))} style={KEY_TOUCH_STYLE} className="flex-1 min-w-0 select-none">'</button>
          {"zxcvbnm".split("").map((ch) => (
            <button key={ch} disabled={disabled} data-key={ch} {...press(() => typeChar(ch), ch)} style={KEY_TOUCH_STYLE} className="flex-1 min-w-0 select-none">{ch}</button>
          ))}
          <button disabled={disabled || !value || (editable && pos === 0)} aria-label="Backspace" {...press(deleteBack)} style={KEY_TOUCH_STYLE} className="lb-key-wide lb-key-fn min-w-0 select-none">⌫</button>
        </div>
        {allowSpace ? (
          <div className="flex justify-center">
            <button disabled={disabled} aria-label="Space" {...press(() => typeChar(" "))} style={KEY_TOUCH_STYLE} className="lb-key-space min-w-0 select-none">{ct("tests.space_key", "space")}</button>
          </div>
        ) : <div className="lb-key-row-blank" aria-hidden="true" />}
      </div>
      ) : (
      <div className="lb-letters flex flex-col gap-1.5 -mx-3" style={{ touchAction: "manipulation" }}>
        <div className="flex gap-1 px-1">
          {"qwertyuiop".split("").map((ch) => (
            <button key={ch} disabled={disabled} data-key={ch} {...press(() => typeChar(ch), ch)} style={KEY_TOUCH_STYLE} className={keyClass}>{ch}</button>
          ))}
        </div>
        <div className="flex gap-1 px-1">
          {"asdfghjkl'".split("").map((ch) => (
            <button key={ch} disabled={disabled} data-key={ch} {...press(() => typeChar(ch), ch)} style={KEY_TOUCH_STYLE} className={keyClass}>{ch}</button>
          ))}
        </div>
        <div className="flex gap-1 px-1">
          {"zxcvbnm".split("").map((ch) => (
            <button key={ch} disabled={disabled} data-key={ch} {...press(() => typeChar(ch), ch)} style={KEY_TOUCH_STYLE} className={keyClass}>{ch}</button>
          ))}
          <button disabled={disabled || !value || (editable && pos === 0)} aria-label="Backspace" {...press(deleteBack)} style={KEY_TOUCH_STYLE} className={`${keyClass} text-slate-500 lb-backspace`}>⌫</button>
        </div>
        {allowSpace && (
          <div className="flex gap-1 px-12">
            <button disabled={disabled} aria-label="Space" {...press(() => typeChar(" "))} style={KEY_TOUCH_STYLE} className={keyClass}>{ct("tests.space_key", "space")}</button>
          </div>
        )}
      </div>
      )}
      </Docked>
    </div>
  );
}
